import React, { useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import IosShareIcon from "@mui/icons-material/IosShare";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PauseIcon from "@mui/icons-material/Pause";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import { FaShoppingBasket } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import AdminColorBasketMiniMap, {
    colorBasketNumberBadgeSx,
} from "../../admin/jigsaw/play/AdminColorBasketMiniMap";
import { decryptText } from "../../services/CryptoUtil";
import { trackEvent } from "../../services/GoogleAnalytics";
import Logger from "../../services/Logger";
import CanvasBoard from "../puzzles/jigsaw/CanvasBoard";
import { BOARD_SIZE, PieceVisual, pieceVisualRenderSizePx } from "../puzzles/jigsaw/PieceVisual";
import { usePagesTranslation } from "../locales";
import encryptedSampleEn from "../howto/puzzle-jigsaw/examples/sample_en_jigsaw_encrypted.json";

/** Illustrative baskets for the 6×6 sample (compact blocks so play crop reads larger). */
const DEMO_BASKETS = [
    { index: 0, dominantColorHex: "#5B9BD5", pieceIds: [0, 1, 2, 6, 7, 8] },
    { index: 1, dominantColorHex: "#E8A45A", pieceIds: [3, 4, 5, 9, 10, 11] },
    { index: 2, dominantColorHex: "#3BA666", pieceIds: [12, 13, 14, 18, 19, 20] },
    { index: 3, dominantColorHex: "#2E7D57", pieceIds: [15, 16, 17, 21, 22, 23] },
    { index: 4, dominantColorHex: "#8B5A2B", pieceIds: [24, 25, 26, 30, 31, 32] },
    { index: 5, dominantColorHex: "#C98400", pieceIds: [27, 28, 29, 33, 34, 35] },
];

const DEMO_BASKET_INDEX = 0;

const CARD_INK = "#163028";
const CARD_MUTED = "rgba(22, 48, 40, 0.78)";

const CARD_PAPER_SX = {
    display: "flex",
    flexDirection: "column",
    gap: { xs: 1.75, sm: 2 },
    p: { xs: 2, sm: 2.5 },
    borderRadius: 3,
    border: "1px solid rgba(255, 255, 255, 0.55)",
    background:
        "linear-gradient(145deg, rgba(232, 241, 248, 0.72) 0%, rgba(255,255,255,0.55) 48%, rgba(210, 230, 255, 0.48) 100%)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 10px 40px rgba(25, 118, 210, 0.08), 0 2px 10px rgba(0, 0, 0, 0.04)",
    color: CARD_INK,
};

const PANEL_SX = {
    height: "100%",
    borderRadius: 2,
    border: "1px solid rgba(0,0,0,0.06)",
    bgcolor: "rgba(255,255,255,0.55)",
    p: 1.25,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 1,
};

const TOOLBAR_MUTED_ICON_SX = {
    fontSize: 20,
    color: "#3BA666",
    opacity: 0.55,
};

/** Static play-page toolbar preview — basket control is enlarged and circled. */
function PlayToolbarBasketHint({ t }) {
    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
            }}
            aria-label={t(
                "puzzle.jigsaw.color-baskets-toolbar-aria",
                "Play toolbar — color baskets button",
            )}
        >
            <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: CARD_MUTED, letterSpacing: "0.02em" }}
            >
                {t("puzzle.jigsaw.color-baskets-toolbar-label", "Play toolbar")}
            </Typography>
            <Box
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: { xs: 0.35, sm: 0.75 },
                    px: { xs: 1, sm: 1.5 },
                    py: 0.85,
                    borderRadius: 999,
                    bgcolor: "rgba(255,255,255,0.92)",
                    border: "1px solid rgba(59, 166, 102, 0.28)",
                    boxShadow: "0 4px 14px rgba(40, 70, 55, 0.1)",
                    maxWidth: "100%",
                    overflowX: "auto",
                }}
            >
                <ArrowBackIcon sx={TOOLBAR_MUTED_ICON_SX} aria-hidden />
                <IosShareIcon sx={TOOLBAR_MUTED_ICON_SX} aria-hidden />
                <PauseIcon sx={TOOLBAR_MUTED_ICON_SX} aria-hidden />
                <CleaningServicesIcon sx={TOOLBAR_MUTED_ICON_SX} aria-hidden />
                <LightbulbOutlinedIcon sx={TOOLBAR_MUTED_ICON_SX} aria-hidden />
                <OpenInNewIcon sx={TOOLBAR_MUTED_ICON_SX} aria-hidden />
                <Box
                    sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        bgcolor: "rgba(59, 166, 102, 0.14)",
                        border: "2.5px solid #3BA666",
                        boxShadow: "0 0 0 4px rgba(59, 166, 102, 0.18)",
                        mx: 0.25,
                    }}
                    aria-label={t("puzzle.jigsaw.color-baskets", "Color baskets")}
                >
                    <FaShoppingBasket size={28} color="#3BA666" aria-hidden />
                </Box>
                <SettingsIcon sx={TOOLBAR_MUTED_ICON_SX} aria-hidden />
            </Box>
            <Typography
                variant="caption"
                sx={{
                    textAlign: "center",
                    lineHeight: 1.4,
                    maxWidth: 420,
                    px: 1,
                    color: CARD_MUTED,
                }}
            >
                {t(
                    "puzzle.jigsaw.color-baskets-toolbar-note",
                    "On the play page, tap the circled basket icon in the toolbar to open color baskets.",
                )}
            </Typography>
        </Box>
    );
}

function SelectBasketsPreview({ imageUrl, grid, t }) {
    return (
        <Box sx={PANEL_SX} aria-hidden>
            <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: CARD_MUTED, letterSpacing: "0.02em" }}
            >
                {t("landing.color-baskets-select-label", "Color Baskets")}
            </Typography>
            {imageUrl && grid > 0 ? (
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 220,
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <AdminColorBasketMiniMap
                        baskets={DEMO_BASKETS}
                        imageUrl={imageUrl}
                        grid={grid}
                        size={null}
                        compact
                        oneBasedLabels
                        highlightBasketIndex={DEMO_BASKET_INDEX}
                        emphasizeOnly
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 220,
                        aspectRatio: "1 / 1",
                        borderRadius: 1,
                        bgcolor: "action.hover",
                    }}
                />
            )}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: 0.75,
                    width: "100%",
                }}
            >
                {DEMO_BASKETS.map((basket) => {
                    const selected = basket.index === DEMO_BASKET_INDEX;
                    return (
                        <Box
                            key={basket.index}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.25,
                                width: 48,
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1,
                                    bgcolor: basket.dominantColorHex,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: selected
                                        ? "0 0 0 2px #1976d2"
                                        : "0 1px 2px rgba(0,0,0,0.18)",
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 2,
                                        left: 2,
                                        ...colorBasketNumberBadgeSx({
                                            size: 14,
                                            highlighted: selected,
                                        }),
                                    }}
                                >
                                    {basket.index + 1}
                                </Box>
                                <FaShoppingBasket size={14} color="#fff" />
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function BasketPlayPreview({ imageUrl, paths, grid, t }) {
    const basket = DEMO_BASKETS[DEMO_BASKET_INDEX];
    const pieceIds = basket.pieceIds;
    const hex = basket.dominantColorHex;

    const boardPieces = useMemo(() => {
        if (!grid || pieceIds.length === 0) return [];
        const cellPx = BOARD_SIZE / grid;
        const onBoardCount = Math.max(1, Math.floor(pieceIds.length / 2));
        return pieceIds.slice(0, onBoardCount).map((pieceIndex) => ({
            id: `landing-basket-${pieceIndex}`,
            pieceIndex,
            x: (pieceIndex % grid) * cellPx,
            y: Math.floor(pieceIndex / grid) * cellPx,
        }));
    }, [grid, pieceIds]);

    const trayIds = useMemo(() => {
        const onBoard = new Set(boardPieces.map((b) => b.pieceIndex));
        return pieceIds.filter((id) => !onBoard.has(id));
    }, [boardPieces, pieceIds]);

    const regionGuide = useMemo(
        () => ({ pieceIndexes: pieceIds, colorHex: hex }),
        [pieceIds, hex],
    );

    const cellPx = grid > 0 ? BOARD_SIZE / grid : 0;
    const nativePx = cellPx > 0 ? pieceVisualRenderSizePx(cellPx) : 48;
    const trayScale = 0.32;
    const trayCollapse = (nativePx * trayScale - nativePx) / 2;

    return (
        <Box
            sx={{
                ...PANEL_SX,
                bgcolor: "rgba(210, 230, 255, 0.55)",
                borderColor: "rgba(25, 118, 210, 0.18)",
                pointerEvents: "none",
                userSelect: "none",
            }}
            aria-hidden
        >
            <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: CARD_MUTED, letterSpacing: "0.02em" }}
            >
                {t("landing.color-baskets-play-label", "Basket play")}
            </Typography>
            <Box
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 0.75,
                    py: 0.3,
                    borderRadius: 1.25,
                    bgcolor: "rgba(255, 244, 230, 0.95)",
                    border: "1px solid rgba(196, 140, 60, 0.35)",
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, fontSize: 11, whiteSpace: "nowrap", color: CARD_INK }}
                >
                    {t("landing.color-baskets-chip-label", "Basket")}
                </Typography>
                <Box sx={{ ...colorBasketNumberBadgeSx({ size: 16 }), flexShrink: 0 }}>1</Box>
                <Box
                    sx={{
                        width: 14,
                        height: 14,
                        borderRadius: 0.5,
                        bgcolor: hex,
                        border: "1px solid rgba(255,255,255,0.85)",
                        flexShrink: 0,
                    }}
                />
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, fontSize: 11, whiteSpace: "nowrap", color: CARD_INK }}
                >
                    {pieceIds.length}
                </Typography>
            </Box>

            {imageUrl && paths && grid > 0 ? (
                <>
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: 300,
                            borderRadius: 1,
                            overflow: "hidden",
                            border: 1,
                            borderColor: "divider",
                            bgcolor: "background.paper",
                        }}
                    >
                        <CanvasBoard
                            boardPieces={boardPieces}
                            grid={grid}
                            imageUrl={imageUrl}
                            paths={paths}
                            placedCount={boardPieces.length}
                            total={pieceIds.length}
                            isSolved={false}
                            showHeader={false}
                            readOnly
                            showDieLines={false}
                            regionGuide={regionGuide}
                            cropToRegionGuide
                            cropMaxHeight={240}
                        />
                    </Box>
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: 300,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.25,
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 32,
                            borderRadius: 1,
                            border: 1,
                            borderColor: "divider",
                            bgcolor: "background.paper",
                            px: 0.5,
                            py: 0.35,
                        }}
                    >
                        {trayIds.map((pieceIndex) => (
                            <Box
                                key={`landing-tray-${pieceIndex}`}
                                sx={{
                                    width: nativePx,
                                    height: nativePx,
                                    transform: `scale(${trayScale})`,
                                    transformOrigin: "center center",
                                    m: `${trayCollapse}px`,
                                    flexShrink: 0,
                                }}
                            >
                                <PieceVisual
                                    pieceIndex={pieceIndex}
                                    imageUrl={imageUrl}
                                    grid={grid}
                                    paths={paths}
                                    showGridCoords={false}
                                    anchorToCell={false}
                                />
                            </Box>
                        ))}
                    </Box>
                </>
            ) : (
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 220,
                        aspectRatio: "1 / 1",
                        borderRadius: 1,
                        bgcolor: "action.hover",
                    }}
                />
            )}
        </Box>
    );
}

/**
 * Landing teaser for jigsaw Color Baskets.
 * Two-column demo (select | play) with short explanation underneath.
 */
export default function LandingColorBasketCard({
    embedded = false,
    showBrowseCta = true,
    showToolbarHint = false,
    title = null,
    body = null,
} = {}) {
    const { t } = usePagesTranslation();
    const [sample, setSample] = useState(null);

    useEffect(() => {
        try {
            const encryptedText =
                typeof encryptedSampleEn === "string"
                    ? encryptedSampleEn
                    : encryptedSampleEn?.data ||
                      encryptedSampleEn?.encrypted ||
                      Object.values(encryptedSampleEn)[0];
            if (!encryptedText || typeof encryptedText !== "string") return;
            const decrypted = decryptText(encryptedText.trim());
            if (decrypted) setSample(JSON.parse(decrypted));
        } catch (e) {
            Logger.error("LandingColorBasketCard: failed to decrypt sample", e);
        }
    }, []);

    const jigsaw = sample?.jigsaw ?? null;
    const story = sample?.story ?? null;
    const imageUrl = story?.imageUrl ?? story?.imagePreviewUrl ?? story?.imageIcon400Url ?? null;
    const paths = jigsaw?.piecePaths?.pieces ?? null;
    const grid = jigsaw?.tileSize ?? 0;

    return (
        <Box
            component={embedded ? "div" : "section"}
            aria-labelledby="landing-color-baskets-heading"
            sx={{
                mb: embedded ? 0 : { xs: 4, sm: 5 },
                maxWidth: embedded ? "none" : 920,
                mx: embedded ? 0 : "auto",
                width: "100%",
            }}
        >
            <Paper elevation={0} sx={CARD_PAPER_SX}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "flex-start", sm: "center" },
                        justifyContent: "space-between",
                        gap: 1.5,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            id="landing-color-baskets-heading"
                            variant="subtitle1"
                            component={embedded ? "h3" : "h2"}
                            sx={{
                                fontWeight: 700,
                                lineHeight: 1.35,
                                fontSize: { xs: "1.05rem", sm: "1.2rem" },
                                color: CARD_INK,
                            }}
                        >
                            {title || t("landing.color-baskets-title", "Color Baskets")}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 1,
                            width: { xs: "100%", sm: "auto" },
                            flexShrink: 0,
                        }}
                    >
                        <Button
                            component={RouterLink}
                            to="/howto/puzzle/jigsaw"
                            variant="contained"
                            size="small"
                            onClick={() => trackEvent("landing_color_baskets_learn", {})}
                            sx={{
                                minHeight: 40,
                                minWidth: { sm: 110 },
                                fontWeight: 700,
                                fontSize: "0.8125rem",
                                textTransform: "none",
                                background:
                                    "linear-gradient(145deg, #1565c0 0%, #1976d2 55%, #42a5f5 100%)",
                                color: "#fff",
                                boxShadow:
                                    "0 8px 24px rgba(25, 118, 210, 0.35), 0 2px 8px rgba(0,0,0,0.08)",
                                "&:hover": {
                                    background:
                                        "linear-gradient(145deg, #0d47a1 0%, #1565c0 50%, #1976d2 100%)",
                                },
                            }}
                        >
                            {t("landing.color-baskets-cta", "See how")}
                        </Button>
                        {showBrowseCta ? (
                            <Button
                                component={RouterLink}
                                to="/puzzles/jigsaw"
                                variant="outlined"
                                size="small"
                                onClick={() => trackEvent("landing_color_baskets_browse", {})}
                                sx={{
                                    minHeight: 40,
                                    minWidth: { sm: 96 },
                                    fontWeight: 700,
                                    fontSize: "0.8125rem",
                                    textTransform: "none",
                                    borderColor: "rgba(25, 118, 210, 0.45)",
                                    color: "#1565c0",
                                    "&:hover": {
                                        borderColor: "#1565c0",
                                        bgcolor: "rgba(25, 118, 210, 0.06)",
                                    },
                                }}
                            >
                                {t("landing.color-baskets-more", "Jigsaws")}
                            </Button>
                        ) : null}
                    </Box>
                </Box>

                {body ? (
                    <Typography
                        variant="body2"
                        sx={{
                            lineHeight: 1.55,
                            fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                            textAlign: "left",
                            color: CARD_MUTED,
                        }}
                    >
                        {body}
                    </Typography>
                ) : null}

                {showToolbarHint ? <PlayToolbarBasketHint t={t} /> : null}

                <Grid container spacing={1.5} sx={{ width: "100%" }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SelectBasketsPreview imageUrl={imageUrl} grid={grid} t={t} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <BasketPlayPreview
                            imageUrl={imageUrl}
                            paths={paths}
                            grid={grid}
                            t={t}
                        />
                    </Grid>
                </Grid>

                {!body ? (
                    <Typography
                        variant="body2"
                        sx={{
                            lineHeight: 1.55,
                            fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                            textAlign: { xs: "left", sm: "center" },
                            maxWidth: 640,
                            mx: "auto",
                            color: CARD_MUTED,
                        }}
                    >
                        {t(
                            "landing.color-baskets-body",
                            "On Medium boards and up, sort pieces by color and assemble one basket at a time. Each color basket costs 5 coins to use, then Deploy onto the main puzzle.",
                        )}
                    </Typography>
                ) : null}
            </Paper>
        </Box>
    );
}
